import React, { useCallback, useState } from "react";

import FormLabel from "@mui/material/FormLabel";

// Components
import Segmented from "../segmented";
import Button from "../button";
import Checkbox from "../Checkbox";
import StarGroup from "../Svgs/StarGroup";
import LanguageSelect from "../LanguageSelect";
import AntSwitch from "../AntSwitch";

// Context
import { useFlashcardSetup } from "./FlashcardSetupContext";
import { useTranslation } from "react-i18next";

// Styles
import styles from "./styles.module.scss";
import Typography from "../Typography";
import LightBulb from "../Svgs/LightBulb";
import useShortCutKey from "../../hooks/useShortCutKey";

const MAX_CARDS = 50;

export default function FlashcardSetupStep() {
  const { t } = useTranslation();
  const { state, handleFieldChange, handleNextStep, handleSubmit, intializedWithSmartNotes, open, languages } = useFlashcardSetup();
  const [error, setError] = useState("");
  const difficultyOptions = [
    { label: t("flashcardSetupModal.step_1.difficulty_options.easy"), value: "easy" },
    { label: t("flashcardSetupModal.step_1.difficulty_options.medium"), value: "medium" },
    { label: t("flashcardSetupModal.step_1.difficulty_options.hard"), value: "hard" },
    { label: t("flashcardSetupModal.step_1.difficulty_options.very_hard"), value: "very_hard" },
  ];

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, "");
    if (val === "" || parseInt(val) <= MAX_CARDS) {
      handleFieldChange({ numberOfCards: val });
      setError("");
    } else {
      setError(t("flashcardSetupModal.errors.max_cards", { count: MAX_CARDS }));
    }
  };

  const handleMainButtonClick = useCallback(() => {
    if (!state.numberOfCards || parseInt(state.numberOfCards) === 0) {
      setError(t("flashcardSetupModal.errors.number_required"));
      return;
    }
    intializedWithSmartNotes ? handleSubmit() : handleNextStep();
  }, [state.numberOfCards, intializedWithSmartNotes, handleSubmit, handleNextStep, t]);
  useShortCutKey({ key: "Enter", onKeyPress: handleMainButtonClick, active: open });

  return (
    <div className={`flex flex-col gap-4 w-full mx-auto ${styles.step} dark:bg-zeus-700`}>
      <div>
        <label
          className="block text-lg font-semibold mb-2"
          htmlFor="number-of-cards"
        >
          1. {t('flashcardSetupModal.step_1.number_of_cards')}
        </label>
        <input
          id="number-of-cards"
          type="text"
          className="number-of-cards-input w-full border rounded-lg px-4 py-2 text-lg text-black dark:bg-stone-650"
          placeholder={t('flashcardSetupModal.step_1.placeholder')}
          value={state.numberOfCards || ""}
          onChange={handleNumberChange}
          maxLength={3}
        />
        {error && <div className="text-red-500 text-sm mt-1">{error}</div>}
      </div>
      <div>
        <label
          className="block text-lg font-semibold mb-2"
          htmlFor="difficulty"
        >
          2. {t('flashcardSetupModal.step_1.difficulty_level')}
        </label>
        <Segmented
          options={difficultyOptions}
          value={state.difficulty}
          onChange={(difficulty) => handleFieldChange({ difficulty })}
          className={`${styles.segmented} dark:bg-zeus-800`}
        />
      </div>
      {languages && (
        <div className="flex flex-col gap-3">
          <FormLabel htmlFor="language-select" className="cursor-pointer">
            {t('quizSetupModal.language')}
          </FormLabel>
          <LanguageSelect value={state.language} onChange={(language) => handleFieldChange({ language })} languages={languages} />
        </div>
      )}
      <div className="flex flex-col gap-2 mt-2">
        <label
          className="flex justify-between gap-2"
          htmlFor="adaptive-mix"
        >
          <div className="flex items-center gap-2">
            <StarGroup />
            <Typography className={styles.label_main}>
              {t('flashcardSetupModal.step_1.adaptive_mix')}
            </Typography>
          </div>

          <AntSwitch
            checked={state.adaptiveMix}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFieldChange({ adaptiveMix: e.target.checked })}
          />
        </label>

        <span className="text-gray-500 text-sm lg:min-w-[400px]">
          {t("flashcardSetupModal.step_1.adaptive_mix_description")}
        </span>

        <label
          className="flex justify-between gap-2 mt-4"
          htmlFor="skipping-hints"
        >
          <div className="flex items-center gap-2">
            <LightBulb />
            <Typography className={styles.label_main}>
              {t('flashcardSetupModal.step_1.skipping_hints')}
            </Typography>
          </div>

          <AntSwitch
            checked={state.skippingHints}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFieldChange({ skippingHints: e.target.checked })}
          />
        </label>
      </div>
      <Button
        variant="contained"
        type="primary"
        role='button'
        className="w-full mt-4 bg-blue-500 text-white py-3 rounded-full text-lg flex items-center justify-center gap-2 sticky bottom-0 primary-button"
        onClick={() => {
          if (!state.numberOfCards || parseInt(state.numberOfCards) === 0) {
            setError("Number of cards is required");
            return;
          }
          intializedWithSmartNotes ? handleSubmit() : handleNextStep();
        }}
      >
        {intializedWithSmartNotes ? <StarGroup stroke="#fff" /> : null }
        {intializedWithSmartNotes ? t('flashcardSetupModal.start_generation') : t('flashcardSetupModal.next')}
      </Button>
    </div>
  );
}
